import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import { fetchPeriodReportAction, setError } from "../slices/periodReportSlice";
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  subYears,
  format,
} from "date-fns";

export type PeriodPreset =
  | "current_month"
  | "last_month"
  | "current_year"
  | "last_year";

export const usePeriodReportBuilder = () => {
  const dispatch = useDispatch();

  const report = useSelector((state: RootState) => state.periodReport.report);
  const loading = useSelector((state: RootState) => state.periodReport.loading);
  const error = useSelector((state: RootState) => state.periodReport.error);

  const [selectedPeriod, setSelectedPeriod] =
    useState<PeriodPreset>("current_month");

  const handlePeriodChange = useCallback(
    (period: PeriodPreset) => {
      setSelectedPeriod(period);
      dispatch(setError(null));
    },
    [dispatch],
  );

  const getDateRange = useCallback(
    (preset: PeriodPreset): { start: Date; end: Date } | null => {
      const today = new Date();
      switch (preset) {
        case "current_month":
          return { start: startOfMonth(today), end: endOfMonth(today) };
        case "last_month": {
          const lastMonth = subMonths(today, 1);
          return {
            start: startOfMonth(lastMonth),
            end: endOfMonth(lastMonth),
          };
        }
        case "current_year":
          return { start: startOfYear(today), end: endOfYear(today) };
        case "last_year": {
          const lastYear = subYears(today, 1);
          return {
            start: startOfYear(lastYear),
            end: endOfYear(lastYear),
          };
        }
        default:
          return null;
      }
    },
    [],
  );

  const handleGenerateReport = useCallback(() => {
    const dateRange = getDateRange(selectedPeriod);
    if (!dateRange) {
      return;
    }

    const periodStart = format(dateRange.start, "yyyy-MM-dd");
    const periodEnd = format(dateRange.end, "yyyy-MM-dd");

    dispatch(
      fetchPeriodReportAction({
        reportType: "profit_and_loss",
        periodStart,
        periodEnd,
      }),
    );
  }, [dispatch, selectedPeriod, getDateRange]);

  // Validate that the selected period can generate a valid date range
  const canGenerate = getDateRange(selectedPeriod) !== null;

  return {
    selectedPeriod,
    setSelectedPeriod: handlePeriodChange,
    report,
    loading,
    error,
    handleGenerateReport,
    canGenerate,
  };
};
